/**
 * Centralized project lifecycle status configuration.
 *
 * Five canonical statuses:
 *   1. Quoted / Under Review
 *   2. Ongoing
 *   3. After Sales / Touch Ups   ← new
 *   4. Completed
 *   5. Cancelled                 ← replaces "Not Awarded" + "Rejected / Cancelled"
 *
 * All display, filtering, badge, and save logic should import from here.
 */

import { naturalSortOptions } from './sortHelpers';

// ── Canonical status keys ────────────────────────────────────────────────────
export const PROJECT_STATUS = {
  QUOTED_UNDER_REVIEW: 'quoted-under-review',
  ONGOING:             'ongoing',
  AFTER_SALES:         'after-sales-touch-ups',
  COMPLETED:           'completed',
  CANCELLED:           'cancelled',

  // ── Backwards-compatibility aliases (resolve to canonical keys above) ──────
  // Any code that still imports these will continue to work; the string value
  // is now the canonical key it maps to.
  NOT_AWARDED:           'cancelled',
  REJECTED_CANCELLED:    'cancelled',
  NOT_AWARDED_CANCELLED: 'cancelled',
};

// ── Normalisation map: any stored value → canonical key ─────────────────────
const NORMALIZE_MAP = {
  // ── Legacy "active" statuses ─────────────────────────────────────────────
  'tender':                     PROJECT_STATUS.QUOTED_UNDER_REVIEW,
  'under-review':               PROJECT_STATUS.QUOTED_UNDER_REVIEW,
  'quoted / under review':      PROJECT_STATUS.QUOTED_UNDER_REVIEW,
  'active':                     PROJECT_STATUS.ONGOING,
  'on-hold':                    PROJECT_STATUS.ONGOING,

  // ── After Sales variants ─────────────────────────────────────────────────
  'after-sales':                PROJECT_STATUS.AFTER_SALES,
  'after sales':                PROJECT_STATUS.AFTER_SALES,
  'touch-ups':                  PROJECT_STATUS.AFTER_SALES,
  'touch ups':                  PROJECT_STATUS.AFTER_SALES,
  'after sales / touch ups':    PROJECT_STATUS.AFTER_SALES,
  'after-sales-touch-ups':      PROJECT_STATUS.AFTER_SALES,

  // ── Cancelled / lost variants (all → single "cancelled") ─────────────────
  'cancelled':                  PROJECT_STATUS.CANCELLED,
  'cancelled / rejected':       PROJECT_STATUS.CANCELLED,
  'rejected':                   PROJECT_STATUS.CANCELLED,
  'rejected / cancelled':       PROJECT_STATUS.CANCELLED,
  'rejected-cancelled':         PROJECT_STATUS.CANCELLED,
  'not-awarded':                PROJECT_STATUS.CANCELLED,
  'not awarded':                PROJECT_STATUS.CANCELLED,
  'not-awarded-cancelled':      PROJECT_STATUS.CANCELLED,
  'not awarded / cancelled':    PROJECT_STATUS.CANCELLED,

  // ── New canonical pass-throughs ──────────────────────────────────────────
  'quoted-under-review':        PROJECT_STATUS.QUOTED_UNDER_REVIEW,
  'ongoing':                    PROJECT_STATUS.ONGOING,
  'completed':                  PROJECT_STATUS.COMPLETED,
};

/**
 * Accepts any old or new status string and returns one of the five canonical keys.
 * Falls back to QUOTED_UNDER_REVIEW if the value is missing or unrecognised.
 */
export const normalizeProjectStatus = (status) => {
  if (!status) return PROJECT_STATUS.QUOTED_UNDER_REVIEW;
  return NORMALIZE_MAP[status.toLowerCase().trim()] || PROJECT_STATUS.QUOTED_UNDER_REVIEW;
};

// ── Dropdown options (value/label pairs for SearchableDropdown) ──────────────
export const PROJECT_STATUS_OPTIONS = [
  { value: PROJECT_STATUS.QUOTED_UNDER_REVIEW, label: 'Quoted / Under Review'    },
  { value: PROJECT_STATUS.ONGOING,             label: 'Ongoing'                  },
  { value: PROJECT_STATUS.AFTER_SALES,         label: 'After Sales / Touch Ups'  },
  { value: PROJECT_STATUS.COMPLETED,           label: 'Completed'                },
  { value: PROJECT_STATUS.CANCELLED,           label: 'Cancelled'                },
];

/** Returns the human-readable label for any status (old or new). */
export const getStatusLabel = (status) => {
  const key = normalizeProjectStatus(status);
  return PROJECT_STATUS_OPTIONS.find(o => o.value === key)?.label || 'Quoted / Under Review';
};

// ── Colour tokens for each canonical status ──────────────────────────────────
export const PROJECT_STATUS_COLORS = {
  [PROJECT_STATUS.QUOTED_UNDER_REVIEW]: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  [PROJECT_STATUS.ONGOING]:             { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  [PROJECT_STATUS.AFTER_SALES]:         { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  [PROJECT_STATUS.COMPLETED]:           { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  [PROJECT_STATUS.CANCELLED]:           { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
};

/**
 * Returns the CSS class suffix for badge / placeholder elements.
 * Always returns a normalised key so CSS only needs the five classes.
 */
export const getStatusClass = (status) => normalizeProjectStatus(status);

// ── Shared group-header styling for project dropdowns grouped by status ─────
// Used by both ProjectDropdown.jsx and any SearchableDropdown instance whose
// options are grouped into Ongoing / After Sales / All Other Projects
// (e.g. the project pickers in CostRowsTable.jsx and WagesTable.jsx) so every
// such dropdown in the app shares one colour scheme.
export const PROJECT_GROUP_STYLES = {
  ongoing:    { label: 'ONGOING PROJECTS',        color: '#92400e', bg: '#fefce8', border: '#fde68a' },
  afterSales: { label: 'AFTER SALES / TOUCH UPS', color: '#6b21a8', bg: '#faf5ff', border: '#e9d5ff' },
  other:      { label: 'ALL OTHER PROJECTS',      color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

/**
 * Build the grouped `options` array for a SearchableDropdown of projects —
 * Ongoing / After Sales / All Other, each under a coloured section header, each
 * naturally sorted by name.
 *
 * Shared so the project picker reads identically wherever it appears (the cost
 * table's per-row cell and the bulk-entry form's per-line cell). Empty groups
 * emit no header. Requires `sort={false}` on the dropdown — the ordering here
 * is the point, and re-sorting would scatter the headers.
 *
 * @param {Array<{id: string, name: string, status: string}>} projects
 * @param {Object} [opts]
 * @param {'id'|'name'} [opts.valueKey='id']  what each option's `value` carries.
 *   'id' is right almost everywhere — a project reference should survive a
 *   rename. 'name' exists for the one field that stores a project as free text
 *   rather than as a reference: Attendance's `dayWorkplaces.workplace`, whose
 *   historical values are typed site addresses from the workbook and which the
 *   Location tab groups by string.
 * @returns {Array<object>} SearchableDropdown options, headers included
 */
export const buildGroupedProjectOptions = (projects, { valueKey = 'id' } = {}) => {
  const ongoing = [], afterSales = [], other = [];
  (projects || []).forEach(p => {
    const norm = normalizeProjectStatus(p.status);
    if (norm === PROJECT_STATUS.ONGOING)          ongoing.push(p);
    else if (norm === PROJECT_STATUS.AFTER_SALES) afterSales.push(p);
    else                                          other.push(p);
  });

  const opts = [];
  const pushGroup = (list, style) => {
    if (list.length === 0) return;
    opts.push({ header: true, ...style });
    naturalSortOptions(list, p => p.name).forEach(p => opts.push({ value: p[valueKey], label: p.name }));
  };
  pushGroup(ongoing,    PROJECT_GROUP_STYLES.ongoing);
  pushGroup(afterSales, PROJECT_GROUP_STYLES.afterSales);
  pushGroup(other,      PROJECT_GROUP_STYLES.other);
  return opts;
};

// ── Raw status value sets for Firestore / client-side query helpers ───────────
/** All raw values that map to "in-progress" projects (not closed). */
export const ACTIVE_RAW_STATUSES = [
  'active', 'on-hold', 'tender', 'under-review', 'ongoing', 'quoted-under-review',
];
/** All raw values that map to after-sales projects. */
export const AFTER_SALES_RAW_STATUSES = [
  'after-sales', 'after sales', 'touch-ups', 'touch ups',
  'after-sales-touch-ups', 'after sales / touch ups',
];
/** All raw values that map to completed projects. */
export const COMPLETED_RAW_STATUSES = ['completed'];
/** All raw values that map to cancelled / lost projects. */
export const CANCELLED_RAW_STATUSES = [
  'cancelled', 'rejected', 'not-awarded', 'not awarded',
  'not-awarded-cancelled', 'rejected-cancelled',
  'cancelled / rejected', 'rejected / cancelled', 'not awarded / cancelled',
];
